import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
} from "amazon-cognito-identity-js";
import { AUTH_ERROR_MESSAGES } from "../constants/auth";

const readOnlyChallengeAttributes = new Set([
  "email_verified",
  "phone_number_verified",
  "sub",
]);

function getCognitoConfig() {
  const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

  if (!userPoolId || !clientId) {
    throw new Error(AUTH_ERROR_MESSAGES.cognitoNotConfigured);
  }

  return { userPoolId, clientId };
}

function getUserPool() {
  const { userPoolId, clientId } = getCognitoConfig();

  return new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
  });
}

function normalizeAuthError(error) {
  if (!error) {
    return new Error(AUTH_ERROR_MESSAGES.signInFailed);
  }

  if (error instanceof Error) {
    return error;
  }

  return new Error(error.message || AUTH_ERROR_MESSAGES.signInFailed);
}

function tokenValue(token) {
  return token?.getJwtToken?.() || token?.getToken?.() || "";
}

function tokenExpirationMs(token) {
  const expirationSeconds = token?.getExpiration?.();

  if (!expirationSeconds) {
    return Date.now();
  }

  return expirationSeconds * 1000;
}

export function createTokenSession(
  authResult,
  username,
  fallbackRefreshToken = "",
) {
  const accessToken = authResult.getAccessToken();
  const idToken = authResult.getIdToken();
  const refreshToken = authResult.getRefreshToken();

  return {
    username,
    accessToken: tokenValue(accessToken),
    idToken: tokenValue(idToken),
    refreshToken: tokenValue(refreshToken) || fallbackRefreshToken,
    expiresAt: tokenExpirationMs(accessToken),
  };
}

export function refreshCognitoSession(session) {
  if (!session?.username || !session?.refreshToken) {
    return Promise.reject(new Error(AUTH_ERROR_MESSAGES.sessionExpired));
  }

  const user = new CognitoUser({
    Username: session.username,
    Pool: getUserPool(),
  });
  const refreshToken = new CognitoRefreshToken({
    RefreshToken: session.refreshToken,
  });

  return new Promise((resolve, reject) => {
    user.refreshSession(refreshToken, (error, authResult) => {
      if (error || !authResult) {
        reject(normalizeAuthError(error));
        return;
      }

      resolve(
        createTokenSession(
          authResult,
          session.username,
          session.refreshToken,
        ),
      );
    });
  });
}

export function signInWithCognito(username, password) {
  const user = new CognitoUser({
    Username: username,
    Pool: getUserPool(),
  });
  const authDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess(authResult) {
        resolve({
          type: "SUCCESS",
          tokenSession: createTokenSession(authResult, username),
        });
      },
      onFailure(error) {
        reject(normalizeAuthError(error));
      },
      newPasswordRequired(userAttributes = {}, requiredAttributes = []) {
        resolve({
          type: "NEW_PASSWORD_REQUIRED",
          challenge: {
            user,
            username,
            userAttributes,
            requiredAttributes,
          },
        });
      },
    });
  });
}

function buildChallengeAttributes(requiredAttributes = [], values = {}) {
  return requiredAttributes.reduce((attributes, attributeName) => {
    const value = values[attributeName];

    if (
      readOnlyChallengeAttributes.has(attributeName) ||
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return attributes;
    }

    return {
      ...attributes,
      [attributeName]: value,
    };
  }, {});
}

export function completeNewPasswordChallenge({
  challenge,
  newPassword,
  attributes,
}) {
  if (!challenge?.user) {
    return Promise.reject(
      new Error(AUTH_ERROR_MESSAGES.passwordResetSessionExpired),
    );
  }

  const challengeAttributes = buildChallengeAttributes(
    challenge.requiredAttributes,
    attributes,
  );

  return new Promise((resolve, reject) => {
    challenge.user.completeNewPasswordChallenge(
      newPassword,
      challengeAttributes,
      {
        onSuccess(authResult) {
          resolve({
            tokenSession: createTokenSession(authResult, challenge.username),
          });
        },
        onFailure(error) {
          reject(normalizeAuthError(error));
        },
      },
    );
  });
}

export function signOutCognitoUser() {
  try {
    getUserPool().getCurrentUser()?.signOut();
  } catch {
    // Local sign-out should still clear the app session if Cognito is not configured.
  }
}
